import { useState, useRef, useEffect } from 'react'

/**
 ** Custom hook used to access the previous value of a property/state variable
 * @param value - the variable to store.
 ** @returns {any} - the previous value of the variable.
 * */
export const usePrevious = (value) => {
  // The ref object is a generic container whose current property is mutable ...
  // ... and can hold any value, similar to an instance property on a class  const ref = useRef()
  const ref = useRef()

  // Store current value in ref
  useEffect(() => {
    ref.current = value
  }, [value]) // Only re-run if value changes
  // Return previous value (happens before update in useEffect above)
  return ref.current
}

/**
 ** Custom hook used to assess if component has been mounted/first render already occured.
 ** @returns {boolean} - true if component has been mounted.
 * */
export const useDidMount = () => {
  const isMountRef = useRef(false)
  useEffect(() => {
    isMountRef.current = true
  }, [])
  return isMountRef.current
}

/**
 ** This hook is designed to compose multiple refs into a single ref.
 ** It takes any number of refs as arguments and returns a single ref that
 ** represents the combined functionality of all the input refs.
 ** @returns {Ref} - true if component has been mounted.
 * */
export function useComposedRefs(...refs) {
  const targetRef = useRef()

  useEffect(() => {
    refs.forEach((ref) => {
      if (!ref) return

      if (typeof ref === 'function') {
        ref(targetRef.current)
      } else {
        ref.current = targetRef.current
      }
    })
  }, [refs])

  return targetRef
}

export const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value)

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value)
    }, delay)

    return () => {
      clearTimeout(handler)
    }
  }, [value, delay])

  return debouncedValue
}
